import type { ProjectId } from 'common/types'

import type {
  ContinueUploadSurvey2dType,
  CrmsAttachment,
  CrmsProjectResult,
  CrmsSearchQuery,
  CubeParamsInfo,
  LastUploadPartInfo,
  SegySetupType,
  UploadFaultType,
  UploadHorizonType,
  UploadId,
  UploadSurvey2dType,
} from '../../types'
import { constructClientDispenser } from '../clientDispenserFactory'
import { axiosInstance } from '../common'
import { convertSetupContent } from '../converters'
import type { FaultFormat, HorizonFormat } from './__generated__/data-contracts'
import { FileClient } from './__generated__/File'
import { HttpClient } from './__generated__/http-client'
import { ImportDbClient } from './__generated__/ImportDb'
import { JobClient } from './__generated__/Job'
import { ProjectSearchClient } from './__generated__/ProjectSearch'
import { SeismicImportClient } from './__generated__/SeismicImport'
import { SeismicLoaderClient } from './__generated__/SeismicLoader'
import { convertToCrmsAttachment } from './converters'
import { convert2dSurveyData, toFile } from './helpers'

const Clients = {
  FileClient,
  ProjectSearchClient,
  ImportDbClient,
  SeismicImportClient,
  JobClient,
  SeismicLoaderClient,
} as const

const getClient = constructClientDispenser(Clients, HttpClient, axiosInstance)

export const initiateMultipartUploadApi = (
  fileName: string,
  totalFileSize: number,
  projectId: ProjectId,
  setupContent: SegySetupType,
): Promise<string> => {
  const client = getClient('FileClient')
  const config = convertSetupContent(setupContent)
  return client.fileInitiateMultipartUpload({
    projectId,
    fileName,
    totalFileSize,
    cubeParamsInfo: config,
  })
}

export const getUncompletedFileInfosApi = async (
  projectId: ProjectId,
): Promise<LastUploadPartInfo[]> => {
  const client = getClient('JobClient')
  return (await client.jobGetNotCompletedImportJobs(projectId)).map(cube => ({
    ...cube,
    projectId,
    cubeParams: cube.cubeParams ?? undefined,
  }))
}

export const multipartUploadApi = (
  uploadId: string,
  isLastPart: boolean,
  file: File,
): Promise<number> => {
  const client = getClient('FileClient')
  return client.fileUploadPart(
    uploadId,
    {
      isLastPart,
    },
    {
      file,
    },
  )
}

export const getMultipartUploadConfigApi = (uploadId: UploadId): Promise<CubeParamsInfo> => {
  const client = getClient('FileClient')
  return client.fileGetCubeConfig(uploadId)
}

export const deleteMultipartUploadFileApi = (uploadId: UploadId): Promise<void> => {
  const client = getClient('JobClient')
  return client.jobRemoveJob({ entityId: uploadId })
}

export const getAllCrmsImportProjectsApi = (
  globalSearch: string,
  query: CrmsSearchQuery,
): Promise<CrmsProjectResult[]> => {
  const client = getClient('ProjectSearchClient')
  return client.projectSearchGetProjects({ globalSearch, ...query })
}

export const getProjectAttachmentsApi = async (
  projectId: ProjectId,
  crmsProjectId: string,
): Promise<CrmsAttachment[]> => {
  const client = getClient('ProjectSearchClient')
  return (await client.projectSearchGetProjectAttachments({ projectId, crmsProjectId })).map(
    convertToCrmsAttachment,
  )
}

export const importCrmsAttachmentApi = (
  projectId: ProjectId,
  attachment: CrmsAttachment,
): Promise<void> => {
  const client = getClient('ImportDbClient')
  return client.importDbImportCrmsSurvey3D({ ...attachment, projectId })
}

export const uploadHorizonApi = (
  projectId: ProjectId,
  { file, name, domainType, cubeUid, survey2DUid }: UploadHorizonType,
): Promise<void> => {
  const client = getClient('SeismicImportClient')
  const horizonFormat: HorizonFormat = 'Charizma'
  const data = {
    File: toFile(file),
    SurveyId: cubeUid ?? survey2DUid,
    ProjectId: projectId,
    Name: name,
    DomainType: domainType,
    HorizonFormat: horizonFormat,
  }
  return cubeUid
    ? client.seismicImportImportHorizonInterpretation3D(data)
    : client.seismicImportImportHorizonInterpretation2D(data)
}

export const uploadFaultApi = (
  projectId: ProjectId,
  { file, name, domainType }: UploadFaultType,
): Promise<void> => {
  const client = getClient('SeismicImportClient')
  const faultFormat: FaultFormat = 'Charizma'
  return client.seismicImportImportFault(projectId, {
    File: toFile(file),
    Name: name,
    DomainType: domainType,
    FaultFormat: faultFormat,
  })
}

export const uploadSurvey2dApi = ({
  files,
  name,
  domainType,
  coordsTemplates,
  projectId,
}: UploadSurvey2dType): Promise<void> => {
  const client = getClient('SeismicImportClient')
  const uploadData = convert2dSurveyData({
    files,
    name,
    domainType,
    coordsTemplates,
    projectId,
  })
  return client.seismicImportImportLocalSurvey2D(uploadData)
}

export const continueUploadSurvey2dApi = ({
  files,
  surveyId,
  surveyName,
  projectId,
  domainType,
}: ContinueUploadSurvey2dType): Promise<void> => {
  const client = getClient('SeismicImportClient')
  return client.seismicImportImportToExistingSurvey2D({
    Files: files?.map(toFile),
    SurveyId: surveyId,
    SurveyName: surveyName,
    ProjectId: projectId,
    DomainType: domainType,
  })
}

export const convertCubeApi = (cubeId: string, cubeParams: SegySetupType): Promise<void> => {
  const client = getClient('SeismicLoaderClient')
  const data = convertSetupContent(cubeParams)
  return client.seismicLoaderSendCube(cubeId, data)
}
